import ReactDataTable from '@axel-dev/react-data-table-component';
import _ from 'lodash';
import React from 'react';
import styled, { css } from 'styled-components';
import { Label, Progressbar } from 'ui/components/styled';
import device from 'ui/theme/device';
import { spacing } from 'ui/theme/utils';
import Pagination from './Pagination';
import SortIcon from './SortIcon';
import SubHeader from './SubHeader';

export const DataTable = ({
  variant = 'default',
  data: initialData,
  filterPlaceholder,
  filterValue,
  filterProps,
  openMobileFilter,
  isSmall,
  onFilter,
  subHeaderTitle,
  subHeaderTitleProps,
  subHeaderActions,
  subHeaderDisposition,
  subHeaderCloseButton,
  paginationPerPage,
  paginationTotalRows,
  paginationRowsPerPageOptions,
  paginationComponentOptions,
  paginationCurrentPage,
  paginationHeight,
  paginationServer = true,
  expandableRowDisabled,
  onRowsPerPageChange,
  onRowClicked,
  onPageChange,
  onPageReset,
  onSort,
  noDataMessage = 'There are no records to display.',
  noPadding,
  noFilter,
  overflow,
  footer,
  advancedSearch = false,
  advancedSearchOpen,
  setAdvancedSearchOpen,
  ...props
}) => {
  const footerRows = React.useMemo(
    () => (!_.isEmpty(footer) ? (_.isArray(footer) ? footer : [footer]) : []),
    [footer]
  );
  const data = React.useMemo(() => {
    if (!initialData?.length) return [];

    if (footerRows.length > 0) {
      const footerData = footerRows.map((row, index) => ({
        id: `footer-${index}`,
        ...row,
      }));
      return [...initialData, ...footerData];
    }

    return initialData;
  }, [initialData, footerRows]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [paginationProps, setPaginationProps] = React.useState();

  React.useEffect(() => {
    if (paginationCurrentPage > 0 && paginationCurrentPage !== currentPage) {
      setCurrentPage(paginationCurrentPage);
    }
  }, [paginationCurrentPage]);

  const setColumnsId = () => {
    if (process.env.NODE_ENV === 'test') {
      props.columns = props.columns.map((col, index) => {
        col.id = index + 1;
        if (!col.selector && _.isString(col.name)) {
          col.selector = col.name.toLowerCase();
        }
        return col;
      });
    }
  };

  setColumnsId();

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const resetCurrentPage = () => {
    setCurrentPage(1);
    if (onPageReset) {
      onPageReset(1);
    }
  };

  const handleSort = (column, sortDirection) => {
    resetCurrentPage();
    if (onSort) {
      onSort(column, sortDirection);
    }
  };

  const handleFilter = (value) => {
    resetCurrentPage();
    if (onFilter) {
      onFilter(value);
    }
  };

  const handleRowClick = (row) => {
    if (onRowClicked && !row.id?.startsWith('footer-')) {
      onRowClicked(row);
    }
  };

  const disableRowExpand = (row) => {
    const isFooter = row.id?.startsWith('footer-');
    if (!isFooter && expandableRowDisabled) {
      return expandableRowDisabled(row);
    }
    return isFooter;
  };

  const customGlobalStyles = {
    subHeader: {
      style: {
        flex: 'initial',
        flexWrap: 'wrap',
        minHeight: 'auto',
        padding: '0',
        paddingTop: '3px',
        paddingRight: '3px',
        paddingLeft: '5px',
      },
    },
  };

  const customMobileStyles = {
    headCells: {
      style: {
        paddingLeft: '5px',
        paddingRight: '5px',
      },
    },
    cells: {
      style: {
        paddingLeft: '5px',
        paddingRight: '5px',
      },
    },
  };

  const customStyles = isSmall
    ? { ...customGlobalStyles, ...customMobileStyles }
    : customGlobalStyles;

  const handlePaginationPropsChange = (props) => setPaginationProps(props);

  return (
    <DataTableContainer overflow={overflow} px={!noPadding && 'm'}>
      <StyledDataTable
        className={`styled-dt ${variant}`}
        data={data}
        noHeader
        progressComponent={<Progressbar mode="indeterminate" height="3px" />}
        noDataComponent={<StyledLabel>{noDataMessage}</StyledLabel>}
        subHeaderComponent={
          <SubHeader
            title={subHeaderTitle}
            titleProps={subHeaderTitleProps}
            actions={subHeaderActions}
            disposition={subHeaderDisposition}
            filterPlaceholder={filterPlaceholder}
            filterValue={filterValue}
            filterProps={filterProps}
            openMobileFilter={openMobileFilter}
            onFilter={handleFilter}
            advancedSearch={advancedSearch}
            advancedSearchOpen={advancedSearchOpen}
            setAdvancedSearchOpen={setAdvancedSearchOpen}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={paginationPerPage}
            rowCount={paginationTotalRows}
            rowsPerPageOptions={paginationRowsPerPageOptions}
            selectAllRowsItem={paginationComponentOptions?.selectAllRowsItem}
            selectAllRowsItemText={paginationComponentOptions?.selectAllRowsItemText}
            onRowsPerPageChange={
              paginationServer ? onRowsPerPageChange : paginationProps?.onChangeRowsPerPage
            }
            CloseButton={subHeaderCloseButton}
            noFilter={noFilter}
            paginationServer={paginationServer}
          />
        }
        subHeader
        persistTableHead
        sortIcon={<SortIcon />}
        customStyles={customStyles}
        pagination
        paginationServer={paginationServer}
        paginationComponent={(paginationDefaultProps) => {
          return paginationServer ? (
            <Pagination
              rowsPerPage={paginationPerPage}
              rowCount={paginationTotalRows}
              onPageChange={handlePageChange}
              currentPage={currentPage}
              componentOptions={paginationComponentOptions}
              minHeight={paginationHeight}
            />
          ) : (
            <Pagination
              onPageChange={paginationDefaultProps.onChangePage}
              componentOptions={paginationComponentOptions}
              minHeight={paginationHeight}
              onPropsChange={!paginationProps ? handlePaginationPropsChange : null}
              {...paginationDefaultProps}
            />
          );
        }}
        onSort={handleSort}
        sortServer
        {...props}
        footerRowsCount={footerRows.length}
        onRowClicked={handleRowClick}
        expandableRowDisabled={disableRowExpand}
      />
    </DataTableContainer>
  );
};

const DataTableContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: ${({ overflow = 'auto' }) => overflow};
  ${(props) => spacing(props)}
`;

const StyledLabel = styled(Label).attrs(() => ({
  variant: 'legend',
  py: 'm',
}))`
  width: 100%;
  text-align: center;
`;

const StyledDataTable = styled(ReactDataTable).attrs(({ rowHeight = '45px' }) => ({
  rowHeight,
}))`
  overflow: auto;
  ${({ zIndex }) => zIndex && `z-index: ${zIndex};`}
  &.outline {
    > div > .rdt_Table {
      border: none;

      > .rdt_TableHead {
        border-top: none;
        > .rdt_TableHeadRow {
          border: 1px solid #ddd;
          min-height: 44px;
          ${({ keepHeadRowPointerEvents }) =>
            keepHeadRowPointerEvents && 'pointer-events: initial;'}
          .rdt_TableCol {
            padding-top: 10px;
            padding-bottom: 10px;
            overflow: hidden;
            word-break: break-word;
            font-weight: 600;
            font-size: 15px;
            @media ${device.small} {
              font-size: 12px;
              padding-top: 5px;
              padding-bottom: 5px;
            }
            letter-spacing: 0.04em;
            line-height: 31px;
            text-align: left;
            color: #333;
            &:not(:first-child) {
              border-left: 1px solid #ddd;
            }
            .rdt_TableCol_Sortable {
              line-height: 21px;
            }
            > div:focus {
              color: #333;
            }
          }
        }
      }

      > .rdt_TableBody {
        border: 1px solid #ddd;
        border-top: none;
        > .rdt_TableRow {
          min-height: ${({ rowHeight }) => rowHeight};
          &:hover {
            background-color: #f5f5f5;
          }
          .rdt_TableCell {
            font-weight: normal;
            font-size: 15px;
            @media ${device.small} {
              font-size: 12px;
            }
            letter-spacing: 0.04em;
            line-height: 31px;
            text-align: left;
            color: #888;
            &:first-child {
              font-weight: 400;
              color: ${({ theme }) => theme.colors.darkGrey};
            }
            &:not(:first-child) {
              border-left: 1px solid #ddd;
            }
          }
        }
      }
    }
  }

  &.greyed {
    > div > .rdt_Table {
      border-bottom: 1px solid #ddd;

      > .rdt_TableHead {
        border-top: none;
        > .rdt_TableHeadRow {
          min-height: 44px;
          background-color: #f9f9f9;
          .rdt_TableCol {
            font-weight: 500;
            font-size: 15px;
            @media ${device.small} {
              font-size: 12px;
            }
            letter-spacing: 0.04em;
            line-height: 31px;
            text-align: left;
            color: #333;
          }
        }
      }

      > .rdt_TableBody {
        > .rdt_TableRow {
          background-color: #f9f9f9;
          min-height: ${({ rowHeight }) => rowHeight};
          .rdt_TableCell {
            font-weight: normal;
            font-size: 15px;
            @media ${device.small} {
              font-size: 12px;
            }
            letter-spacing: 0.04em;
            line-height: 31px;
            text-align: left;
            color: #888;
            &:first-child {
              font-weight: 400;
              color: ${({ theme }) => theme.colors.darkGrey};
            }
          }
        }
      }
    }
  }

  &.default {
    > div > .rdt_Table {
      border: 1px solid #ddd;

      > .rdt_TableHead {
        > .rdt_TableHeadRow {
          min-height: 44px;
          background-color: #f5f9ff;
          .rdt_TableCol {
            font-weight: 500;
            font-size: 15px;
            @media ${device.small} {
              font-size: 12px;
            }
            letter-spacing: 0.04em;
            line-height: 31px;
            text-align: left;
            color: #333;
          }
        }
      }

      > .rdt_TableBody {
        > .rdt_TableRow {
          min-height: ${({ rowHeight }) => rowHeight};
          &:hover {
            background-color: #f5f5f5;
          }
          .rdt_TableCell {
            font-weight: normal;
            font-size: 15px;
            @media ${device.small} {
              font-size: 12px;
            }
            letter-spacing: 0.04em;
            line-height: 31px;
            text-align: left;
            color: #888;
          }
          &:not([id^='row-footer']) {
            .rdt_TableCell:first-child {
              font-weight: 500;
              color: ${({ theme }) => theme.colors.primary};
            }
          }
        }
      }

      ${({ expandableRows, expandableRowsHideExpander }) =>
        expandableRows &&
        !expandableRowsHideExpander &&
        css`
          > .rdt_TableBody > .rdt_TableRow {
            > div:first-child {
              button {
                background: transparent;
                color: ${({ theme }) => theme.colors.primary};
                fill: ${({ theme }) => theme.colors.primary};
              }
            }
            &:not([id^='row-footer']) {
              .rdt_TableCell:nth-child(2) {
                font-weight: 500;
                color: ${({ theme }) => theme.colors.primary};
              }
            }
          }
          > .rdt_TableHead > .rdt_TableHeadRow,
          > .rdt_TableBody > .rdt_TableRow[id^='row-footer'] {
            > div:first-child {
              display: none;
            }
            > div:nth-child(2) {
              ${({ expandableIconWidth }) =>
                expandableIconWidth &&
                `
                  padding-left: 16px;
                  margin-right: ${expandableIconWidth};
                `}
            }
          }
          > .rdt_TableBody > .rdt_TableRow,
          > .rdt_TableHead > .rdt_TableHeadRow {
            > div:first-child {
              ${({ expandableIconWidth }) =>
                expandableIconWidth && `flex: 0 0 ${expandableIconWidth};`}
            }
          }
        `}
    }
  }

  ${({ stickyHeader = true }) =>
    stickyHeader &&
    css`
      > div > .rdt_Table > .rdt_TableHead {
        position: sticky;
        top: 0;
        left: 0;
        z-index: 1;
        ${({ variant }) =>
          variant !== 'outline' &&
          `
          border-top: 1px solid #ddd;
          margin-top: -1px;
        `}
      }
    `}

  ${({ footerRowsCount, stickyFooter = true }) => {
    if (footerRowsCount > 0) {
      const createCSS = (rowHeight) => {
        let styles = '';
        for (let i = 0; i < footerRowsCount - 1; i++) {
          styles += `
            .rdt_TableRow#row-footer-${i} {
              bottom: calc(${rowHeight} * ${footerRowsCount - (i + 1)});
            }
          `;
        }
        return styles;
      };

      return css`
        > div > .rdt_Table {
          > .rdt_TableBody {
            > .rdt_TableRow[id^='row-footer'] {
              ${stickyFooter &&
              `
                position: sticky;
                bottom: 0;
                left: 0;
              `}
              background-color: #f5f9ff;
              border-bottom: 1px solid #ddd;
              //pointer-events: none;
              cursor: initial;
              &:hover {
                background-color: #f5f9ff;
              }
              &:last-of-type {
                margin-bottom: -1px;
              }
              .rdt_TableCell {
                font-weight: 500;
                color: #333;
              }
              ${({ expandableRows, expandableRowsHideExpander }) =>
                expandableRows &&
                !expandableRowsHideExpander &&
                `
                > div:first-child > button {
                  display: none;
                }
              `}
            }
            ${({ rowHeight }) => stickyFooter && createCSS(rowHeight)}
          }
        }
      `;
    }
  }}
`;

export const DataTableCell = styled.div.attrs(({ children, title, wrapped, wrapperProps }) => {
  let attrs = {
    'data-tag': 'allowRowEvents',
    children: wrapped ? (
      <DataTableCellWrapper {...wrapperProps}>{children}</DataTableCellWrapper>
    ) : (
      children
    ),
  };

  if (title) {
    attrs = { ...attrs, title };
  }

  return attrs;
})`
  ${({ textOverflow = 'ellipsis' }) => {
    switch (textOverflow) {
      case 'ellipsis':
        return css`
          white-space: nowrap;
          overflow: ${({ overflow = 'hidden' }) => overflow};
          text-overflow: ellipsis;
        `;
      default:
        return css`
          word-break: break-word;
          line-height: 21px;
        `;
    }
  }}
  ${({ color }) => color && `color: ${color};`}
  ${({ bold }) => bold && 'font-weight: 500;'}
  ${({ expanded }) =>
    expanded &&
    `
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
  `}
  ${({ centered }) =>
    centered &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
  `}
  ${(props) => spacing(props)}
`;

// adding a div wrapper is useful when having floating childrens
const DataTableCellWrapper = styled.div`
  ${(props) => spacing(props)}
`;

export const DataTableColumn = styled.span`
  ${({ noHover }) => noHover && `color: initial;`}
`;
